import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { StoreBadges } from "@/components/StoreBadges";
import { useLanguage } from "@/i18n/LanguageContext";
import { APPS } from "@/data/apps";

const Showcase = () => {
  const { lang, t } = useLanguage();
  const gridRef = useRef(null);

  // Uniforma l'altezza di tutte le card a quella della più alta (ricalcola su resize e cambio lingua)
  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    const normalize = () => {
      const cards = Array.from(grid.querySelectorAll("[data-card]"));
      cards.forEach((c) => { c.style.minHeight = ""; });
      const max = Math.max(...cards.map((c) => c.offsetHeight));
      cards.forEach((c) => { c.style.minHeight = `${max}px`; });
    };
    normalize();
    const timer = setTimeout(normalize, 600);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(normalize);
    window.addEventListener("resize", normalize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", normalize);
    };
  }, [lang]);
  return (
    <section id="apps" className="px-6 py-28 md:px-12 md:py-40" data-testid="apps-section">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-neon">{t.showcase.kicker}</p>
          <h2 className="max-w-3xl font-display text-4xl font-extrabold uppercase tracking-tight sm:text-5xl lg:text-6xl">
            {t.showcase.title}
          </h2>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-muted2 md:text-base">{t.showcase.sub}</p>
        </Reveal>

        <div ref={gridRef} className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="apps-grid">
          {APPS.map((app, idx) => {
            const descClamp = app.appleOnly ? "line-clamp-6" : "line-clamp-3";
            return (
            <Reveal key={app.slug} delay={0.06 * (idx % 3)} className="h-full">
              <div
                data-card
                data-testid={`showcase-${app.slug}`}
                className="group flex h-full flex-col border p-6 transition-colors duration-500 hover:border-[var(--acc)]"
                style={{ "--acc": app.accent, backgroundColor: `${app.accent}0D`, borderColor: `${app.accent}2E` }}
              >
                <Link to={`/apps/${app.slug}`} className="flex items-start gap-4">
                  {app.icon ? (
                    <img
                      src={app.icon}
                      alt={app.name}
                      loading="lazy"
                      className="h-14 w-14 shrink-0 rounded-2xl border border-white/15 transition-transform duration-500 group-hover:scale-105"
                      data-testid={`showcase-icon-${app.slug}`}
                    />
                  ) : (
                    <div
                      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-dashed font-display text-sm font-bold"
                      style={{ borderColor: `${app.accent}80`, color: app.accent }}
                    >
                      S&F
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="min-h-[2.8rem] font-display text-lg font-bold uppercase leading-tight tracking-tight">{app.name}</h3>
                    <p className="mt-1.5 font-mono text-[10px] uppercase tracking-widest" style={{ color: app.accent }}>
                      {app.category[lang]}
                      {app.comingSoon && ` · ${t.showcase.comingSoon}`}
                    </p>
                  </div>
                  <span className="ml-auto shrink-0 font-mono text-xs font-bold" style={{ color: `${app.accent}90` }}>
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                </Link>
                <p className={`mt-4 ${descClamp} text-sm leading-relaxed text-muted2`} data-testid={`showcase-tagline-${app.slug}`}>
                  {app.intro[lang]}
                </p>
                <div className="mt-auto pt-5">
                  <StoreBadges app={app} compact />
                  <Link
                    to={`/apps/${app.slug}`}
                    data-testid={`showcase-open-${app.slug}`}
                    className="mt-4 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em]"
                    style={{ color: app.accent }}
                  >
                    {t.showcase.open}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </Link>
                </div>
              </div>
            </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Showcase;
